import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import AIReviewedChip from "./AIReviewedChip";
import DataSourceIcon from "../../Components/DataSourcesPanel";

const AiSuggestedPlanSection = ({
  theme,
  hasPlanSection,
  shortPlan,
  longPlan,
  entityId,
  AiSectionFeedback,
  requestId,
  uiSectionKey,
  userEmail,
}) => {
  if (!hasPlanSection) return null;

  return (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: "0 10px 25px rgba(15,23,42,0.12)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent sx={{ p: 3.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip
              title="Short- and long-term roadmap suggested by AI."
              arrow
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  color: theme.palette.primary.main,
                  cursor: "help",
                }}
              >
                AI-Suggested Plan (Short-Term vs Long-Term)
              </Typography>
            </Tooltip>
            <AIReviewedChip />
            <DataSourceIcon mode="light" sources={["Salesforce", "Nasdaq"]} />
          </Box>

          <AiSectionFeedback
            sectionName="ClientPortfolio - AI Suggested Plan (Short/Long) (AI-reviewed)"
            entityId={entityId}
            variant="light"
            requestId={requestId}
            uiSectionKey={uiSectionKey}
            userEmail={userEmail}
          />
        </Box>

        <Typography
          sx={{
            fontSize: "0.8rem",
            mb: 2,
            color: theme.palette.text.secondary,
          }}
        >
          Consolidated view of recommended focus areas based on current risks,
          opportunities, and engagement signals.
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
          }}
        >
          {/* SHORT TERM */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "rgba(56,189,248,0.04)",
              border: "1px solid rgba(56,189,248,0.15)",
            }}
          >
            <Typography sx={{ fontWeight: 700, color: "#0369a1", mb: 1 }}>
              🔵 Short-Term Plan (Next 3–6 Months)
            </Typography>

            {shortPlan.length > 0 ? (
              <List dense>
                {shortPlan.map((p, idx) => (
                  <ListItem key={idx} sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 26 }}>
                      <ArrowRightAltIcon
                        sx={{ fontSize: 16, color: "#0369a1" }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={p}
                      primaryTypographyProps={{ fontSize: "0.8rem" }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#6b7280",
                  fontStyle: "italic",
                }}
              >
                —
              </Typography>
            )}
          </Box>

          {/* LONG TERM */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "rgba(22,163,74,0.04)",
              border: "1px solid rgba(22,163,74,0.15)",
            }}
          >
            <Typography sx={{ fontWeight: 700, color: "#166534", mb: 1 }}>
              🟢 Long-Term Plan (Next 6–24 Months)
            </Typography>

            {longPlan.length > 0 ? (
              <List dense>
                {longPlan.map((p, idx) => (
                  <ListItem key={idx} sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 26 }}>
                      <ArrowRightAltIcon
                        sx={{ fontSize: 16, color: "#166534" }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={p}
                      primaryTypographyProps={{ fontSize: "0.8rem" }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#6b7280",
                  fontStyle: "italic",
                }}
              >
                —
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AiSuggestedPlanSection;
